import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { StatutoryRegister, StatutoryTabs } from "@/components/StatutoryRegister";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Plus, X, Receipt, Printer, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  partial: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
};

export default function Tax() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [demands, setDemands] = useState([]);
  const [filter, setFilter] = useState("");
  const [regTab, setRegTab] = useState("module");
  const [showCollect, setShowCollect] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [properties, setProperties] = useState([]);

  const loadDemands = () => {
    const params = {};
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    if (filter) params.status = filter;
    api.get("/tax/demands", { params }).then((r) => setDemands(r.data));
  };

  useEffect(() => { loadDemands(); }, [filter, user]);
  useEffect(() => {
    if (user?.panchayat_id) {
      api.get("/properties", { params: { panchayat_id: user.panchayat_id } }).then((r) => setProperties(r.data));
    } else {
      api.get("/properties").then((r) => setProperties(r.data));
    }
  }, [user]);

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("tax")}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">{t("tax")}</h1>
            <p className="mt-1 text-sm text-slate-600">{lang === "hi" ? "मांग जारी करें, संग्रह करें, रसीद प्रिंट करें।" : "Generate demand, collect tax, print receipts."}</p>
          </div>
          {regTab === "module" && (
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="tax-filter">
              <option value="">All status</option>
              <option value="pending">{t("pending")}</option>
              <option value="partial">{t("partial")}</option>
              <option value="paid">{t("paid")}</option>
            </select>
            <button
              data-testid="generate-demand-btn"
              onClick={() => setShowGenerate(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}
            >
              <Plus className="w-4 h-4" /> {t("newDemand")}
            </button>
          </div>
          )}
        </div>

        <StatutoryTabs moduleLabel={lang === "hi" ? "मांग एवं रसीदें" : "Demands & Receipts"} codes={["IV", "XVIII"]} active={regTab} onChange={setRegTab} />
        {regTab !== "module" && <StatutoryRegister code={regTab} />}

        <div className={regTab === "module" ? "" : "hidden"}>
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="tax-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">{t("financialYear")}</th>
                <th className="text-left px-4 py-3">{t("propertyNo")}</th>
                <th className="text-left px-4 py-3">{t("owner")}</th>
                <th className="text-left px-4 py-3">{t("ward")}</th>
                <th className="text-right px-4 py-3">{t("totalAmount")}</th>
                <th className="text-right px-4 py-3">{t("paidAmount")}</th>
                <th className="text-center px-4 py-3">{t("status")}</th>
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {demands.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{d.financial_year}</td>
                  <td className="px-4 py-3 font-mono text-xs">{d.property_no}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{d.owner_name}</td>
                  <td className="px-4 py-3">{d.ward}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatINR(d.total_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatINR(d.paid_amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-semibold ${STATUS_COLORS[d.status]}`}>{t(d.status)}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap space-x-1">
                    {d.status !== "paid" && (
                      <button
                        data-testid={`collect-${d.id}`}
                        onClick={() => setShowCollect(d)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-white" style={{ background: "#E87722" }}
                      >
                        <Receipt className="w-3 h-3" /> {t("pay")}
                      </button>
                    )}
                    <button
                      data-testid={`sms-${d.id}`}
                      onClick={async () => {
                        const panchayat = await api.get(`/panchayats/${d.panchayat_id}`).then(r => r.data.name);
                        const outstanding = (d.total_amount - d.paid_amount).toFixed(0);
                        try {
                          const r = await api.post("/sms/send", {
                            panchayat_id: d.panchayat_id, mobile: "9876543210",
                            template_key: "demand_bill", language: lang === "hi" ? "hi" : "en",
                            context: { name: d.owner_name, fy: d.financial_year, amount: outstanding, due_date: d.due_date, panchayat },
                            ref_type: "demand", ref_id: d.id,
                          });
                          toast.success(`SMS sent (DEMO): ${r.data.body.slice(0, 60)}…`);
                        } catch (e) { toast.error("Failed"); }
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">
                      <Send className="w-3 h-3" /> SMS
                    </button>
                  </td>
                </tr>
              ))}
              {demands.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">No demands found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {showCollect && (
        <CollectModal
          demand={showCollect}
          onClose={() => setShowCollect(null)}
          onPaid={(receipt) => {
            setShowCollect(null);
            loadDemands();
            toast.success(receipt.approval_status === "approved" ? "Receipt issued" : "Submitted for approval");
            nav(`/tax/receipts/${receipt.id}`);
          }}
        />
      )}

      {showGenerate && (
        <GenerateModal
          properties={properties}
          onClose={() => setShowGenerate(false)}
          onCreated={() => { setShowGenerate(false); loadDemands(); }}
        />
      )}
    </OfficerLayout>
  );
}

function CollectModal({ demand, onClose, onPaid }) {
  const { t } = useLang();
  const [amount, setAmount] = useState(String(demand.total_amount - demand.paid_amount));
  const [mode, setMode] = useState("cash");
  const [ref, setRef] = useState("");
  const [payer, setPayer] = useState(demand.owner_name || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/tax/collect", {
        demand_id: demand.id,
        amount: parseFloat(amount),
        payment_mode: mode,
        payment_ref: ref || null,
        payer_name: payer || null,
      });
      onPaid(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const outstanding = demand.total_amount - demand.paid_amount;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="collect-modal">
      <div className="bg-white rounded-md w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{t("collectTax")}</div>
            <h2 className="font-heading text-xl font-bold mt-1">Receive Payment</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="rounded-md bg-slate-50 p-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t("propertyNo")}</span><span className="font-mono">{demand.property_no}</span></div>
            <div className="flex justify-between mt-1"><span className="text-slate-500">{t("owner")}</span><span className="font-medium">{demand.owner_name}</span></div>
            <div className="flex justify-between mt-1"><span className="text-slate-500">{t("totalAmount")}</span><span className="font-mono">{formatINR(demand.total_amount)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-slate-500">{t("paidAmount")}</span><span className="font-mono">{formatINR(demand.paid_amount)}</span></div>
            <div className="flex justify-between mt-1 pt-1 border-t border-slate-200"><span className="font-semibold">{t("outstanding")}</span><span className="font-mono font-bold">{formatINR(outstanding)}</span></div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Amount (₹)</label>
            <input data-testid="collect-amount" type="number" step="0.01" min="0.01" max={outstanding} required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-base font-mono outline-none focus:border-[#0A3161]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("paymentMode")}</label>
              <select data-testid="collect-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
                {["cash", "upi", "cheque", "card", "online"].map((m) => <option key={m} value={m}>{t(m)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Reference</label>
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="UTR / Cheque #"
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-[#0A3161]" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Payer Name</label>
            <input value={payer} onChange={(e) => setPayer(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-[#0A3161]" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300">{t("cancel")}</button>
            <button type="submit" disabled={busy} data-testid="collect-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#E87722" }}>
              {busy ? "…" : t("collectTax")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GenerateModal({ properties, onClose, onCreated }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    property_id: properties[0]?.id || "",
    financial_year: "2025-26",
    house_tax: "", water_tax: "600", street_light_tax: "200",
    conservancy_tax: "300", drainage_tax: "200",
  });
  const [busy, setBusy] = useState(false);

  // Auto-calc house_tax from selected property
  useEffect(() => {
    const p = properties.find((x) => x.id === form.property_id);
    if (p && !form.house_tax) {
      setForm((f) => ({ ...f, house_tax: String(Math.round(p.annual_rental_value * 0.10)) }));
    }
  }, [form.property_id, properties]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tax/demands", {
        property_id: form.property_id,
        financial_year: form.financial_year,
        house_tax: parseFloat(form.house_tax) || 0,
        water_tax: parseFloat(form.water_tax) || 0,
        street_light_tax: parseFloat(form.street_light_tax) || 0,
        conservancy_tax: parseFloat(form.conservancy_tax) || 0,
        drainage_tax: parseFloat(form.drainage_tax) || 0,
      });
      toast.success("Demand generated");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const total = ["house_tax", "water_tax", "street_light_tax", "conservancy_tax", "drainage_tax"]
    .reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="generate-modal">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{t("newDemand")}</div>
            <h2 className="font-heading text-xl font-bold mt-1">Generate Tax Demand</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Property</label>
            <select required value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}
              data-testid="gen-property" className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
              <option value="">Select property</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.property_no} — {p.owner_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("financialYear")}</label>
            <input required value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm" />
          </div>
          {[
            { k: "house_tax", l: t("houseTax") },
            { k: "water_tax", l: t("waterTax") },
            { k: "street_light_tax", l: t("streetLightTax") },
            { k: "conservancy_tax", l: t("conservancyTax") },
            { k: "drainage_tax", l: t("drainageTax") },
          ].map((f) => (
            <div key={f.k} className="grid grid-cols-2 items-center gap-2">
              <label className="text-sm text-slate-700">{f.l}</label>
              <input type="number" step="0.01" value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm font-mono text-right" />
            </div>
          ))}
          <div className="grid grid-cols-2 items-center gap-2 pt-3 border-t border-slate-100">
            <div className="font-semibold text-slate-900">{t("totalAmount")}</div>
            <div className="text-right font-heading text-xl font-bold">{formatINR(total)}</div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300">{t("cancel")}</button>
            <button type="submit" disabled={busy} data-testid="gen-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
              {busy ? "…" : "Generate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
